// Monitor for runtime errors during migration
export class MigrationMonitor {
  constructor() {
    this.errors = [];
    this.warnings = [];
    this.performanceMarks = new Map();
    
    // Capture console errors
    this.originalConsoleError = console.error;
    console.error = (...args) => {
      this.errors.push({
        message: args.join(' '),
        timestamp: new Date().toISOString(),
        stack: new Error().stack
      });
      this.originalConsoleError(...args);
    };
    
    // Capture console warnings
    this.originalConsoleWarn = console.warn;
    console.warn = (...args) => {
      this.warnings.push({
        message: args.join(' '),
        timestamp: new Date().toISOString()
      });
      this.originalConsoleWarn(...args);
    };
  }
  
  startPerformanceTracking(label) {
    this.performanceMarks.set(label, performance.now());
  }
  
  endPerformanceTracking(label) {
    const startTime = this.performanceMarks.get(label);
    if (startTime) {
      const duration = performance.now() - startTime;
      console.log(`Performance: ${label} took ${duration.toFixed(2)}ms`);
      return duration;
    }
  }
  
  getReport() {
    return {
      errors: this.errors,
      warnings: this.warnings,
      errorCount: this.errors.length,
      warningCount: this.warnings.length,
      hasIssues: this.errors.length > 0
    };
  }
  
  reset() {
    this.errors = [];
    this.warnings = [];
    this.performanceMarks.clear();
  }
  
  destroy() {
    console.error = this.originalConsoleError;
    console.warn = this.originalConsoleWarn;
  }
}

// Create a safe monitor instance that doesn't interfere with React error handling
class SafeMigrationMonitor {
  constructor() {
    this.errors = [];
    this.warnings = [];
    this.performanceMarks = new Map();
    this.isMonitoring = false;
  }
  
  startMonitoring() {
    if (this.isMonitoring) return;
    this.isMonitoring = true;
    
    // Only intercept console methods when explicitly monitoring
    this.originalConsoleError = console.error;
    this.originalConsoleWarn = console.warn;
    
    // Use a more cautious approach - don't intercept React errors
    console.error = (...args) => {
      const message = args.join(' ');
      // Skip React-specific errors to avoid infinite loops
      if (!message.includes('Warning:') && !message.includes('setState') && !message.includes('React')) {
        this.errors.push({
          message,
          timestamp: new Date().toISOString(),
          stack: new Error().stack
        });
      }
      this.originalConsoleError(...args);
    };
  }
  
  stopMonitoring() {
    if (!this.isMonitoring) return;
    this.isMonitoring = false;
    
    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
    }
    if (this.originalConsoleWarn) {
      console.warn = this.originalConsoleWarn;
    }
  }
  
  reset() {
    this.errors = [];
    this.warnings = [];
    this.performanceMarks.clear();
  }
  
  startPerformanceTracking(label) {
    this.performanceMarks.set(label, performance.now());
  }
  
  endPerformanceTracking(label) {
    const startTime = this.performanceMarks.get(label);
    if (startTime) {
      const duration = performance.now() - startTime;
      console.log(`Performance: ${label} took ${duration.toFixed(2)}ms`);
      return duration;
    }
  }
  
  getReport() {
    return {
      errors: this.errors,
      warnings: this.warnings,
      errorCount: this.errors.length,
      warningCount: this.warnings.length,
      hasIssues: this.errors.length > 0
    };
  }
  
  destroy() {
    this.stopMonitoring();
  }
}

// Global monitor instance for development - safe version
export const migrationMonitor = new SafeMigrationMonitor();

// Helper for checkpoint testing
export const runMigrationCheckpoint = async (checkpointName, testFunction) => {
  console.log(`🔍 Running checkpoint: ${checkpointName}`);
  migrationMonitor.reset();
  migrationMonitor.startPerformanceTracking(checkpointName);
  
  try {
    await testFunction();
    const duration = migrationMonitor.endPerformanceTracking(checkpointName);
    const report = migrationMonitor.getReport();
    
    if (report.hasIssues) {
      console.error(`❌ Checkpoint ${checkpointName} failed:`, report);
      return false;
    } else {
      console.log(`✅ Checkpoint ${checkpointName} passed (${duration.toFixed(2)}ms)`);
      return true;
    }
  } catch (error) {
    console.error(`💥 Checkpoint ${checkpointName} crashed:`, error);
    return false;
  }
};